import React from 'react';
import {
    BlankModal,
    cssVar,
    HStack,
    Icon,
    ModalBody,
    ModalFooter,
    ModalHeader,
    useToast,
    VStack,
    Button,
    Spinner,
    InlineBanner,
    Text,
} from '@cad-arc';
import { useForm } from 'react-hook-form';
import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { FormTextarea } from '../../forms/integrated-form-controls/form-text-area';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { useFullscreenModalControls } from '../../modals/context/modal-context-provider';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { useApiClient } from '~/api-client';
import { DebugResource, DebugStatusEnum } from '@mark43/cad-api';
import { getMetricTags } from '@m43/core';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { electronBridge } from '../../electron-bridge/electron-bridge';

type SubmitErrorReportForm = {
    comments: string;
};

type MaybeScreenshots =
    | undefined
    | 'ERROR_GETTING_SCREENSHOTS'
    | {
          title: string;
          content: Blob;
      }[];

export type SubmitErrorReportModalComponentType = typeof SubmitErrorReportModal;

export function getErrorReportScreenshots(): Promise<MaybeScreenshots> {
    if (electronBridge) {
        return electronBridge
            .getErrorReportScreenshots()
            .then(async (screenShotsOrError) => {
                if (Array.isArray(screenShotsOrError)) {
                    const mappedScreenshotPromises = screenShotsOrError.map(
                        async (screenshot, index) => {
                            const blob = await (await fetch(screenshot.data)).blob();
                            return {
                                title: screenshot.title
                                    ? `${screenshot.title}.png`
                                    : `window${index + 1}.png`,
                                content: blob,
                            };
                        }
                    );
                    try {
                        return Promise.all(mappedScreenshotPromises).then((mappedScreenshots) => {
                            return mappedScreenshots;
                        });
                    } catch (e) {
                        return 'ERROR_GETTING_SCREENSHOTS' as const;
                    }
                } else {
                    return screenShotsOrError;
                }
            })
            .catch(() => {
                return 'ERROR_GETTING_SCREENSHOTS' as const;
            });
    } else {
        return Promise.resolve(undefined);
    }
}

function getBrowserNetworkInfo(): {
    type: string;
    effectiveType: string;
    downlink: string;
    rtt: string;
} {
    // @ts-expect-error the type for navigator.connection is incorrect
    const { effectiveType, downlink, rtt, type } = navigator.connection ?? {};
    return {
        effectiveType,
        downlink,
        rtt,
        type,
    };
}

function MutationTile(props: {
    mutation: UseMutationResult<any, any, any, any>;
    loadingText: string;
    failureText: string;
    successText: string;
}) {
    return (
        <HStack
            width="100%"
            borderRadius="md"
            backgroundColor="surface.accent"
            alignItems="center"
            p="3"
        >
            {props.mutation.isLoading ? (
                <Spinner size="sm" />
            ) : props.mutation.isSuccess ? (
                <Icon icon="Check" color="positive.default" />
            ) : props.mutation.isError ? (
                <Icon icon="CloseX" color="negative.default" />
            ) : undefined}
            <Text variant="bodyMd">
                {props.mutation.isLoading
                    ? props.loadingText
                    : props.mutation.isSuccess
                    ? props.successText
                    : props.mutation.isError
                    ? props.failureText
                    : '...'}
            </Text>
            {props.mutation.isError ? (
                <Button
                    variant="ghost"
                    onClick={() => {
                        props.mutation.mutate(props.mutation.variables);
                    }}
                    size="sm"
                >
                    Retry Upload
                </Button>
            ) : undefined}
        </HStack>
    );
}

export function SubmitErrorReportModal(props: {
    screenshots: MaybeScreenshots;
}): React.ReactElement {
    const { isElectron } = useApplicationType();
    const screenshots = props.screenshots;
    const form = useForm<SubmitErrorReportForm>({
        defaultValues: {
            comments: '',
        },
        mode: 'onBlur',
    });
    const toast = useToast();
    const apiClient = useApiClient();
    const uploadSystemInformationMutation = useMutation(async (args: { debugReportId: number }) => {
        const {
            processes,
            networkInterfaces,
            wifiInterfaces,
            wifiConnections,
            ...systemInformation
        } = (await electronBridge?.getSystemInformation?.()) ?? {};
        const systemInformationBlob = new Blob([JSON.stringify(systemInformation)], {
            type: 'application/json',
        });
        const processesBlob = new Blob([JSON.stringify(processes)], {
            type: 'application/json',
        });
        const networkObj = {
            browser: getBrowserNetworkInfo(),
            networkInterfaces,
            wifiInterfaces,
            wifiConnections,
        };
        const networkBlob = new Blob([JSON.stringify(networkObj)]);
        const form = new FormData();
        form.append('SYSTEM_INFO', systemInformationBlob, 'system_information.json');
        form.append('PROCESSES', processesBlob, 'processes.json');
        form.append('NETWORK', networkBlob, 'network.json');
        return apiClient.request<DebugResource.Upload>({
            method: 'POST',
            url: `debug/next/file_upload/${args.debugReportId}/`,
            params: {
                status: DebugStatusEnum.PARTIAL.name,
            },
            data: form,
            timeout: 120000,
        });
    });
    const uploadScreenshotsMutation = useMutation(async (args: { debugReportId: number }) => {
        const form = new FormData();
        if (Array.isArray(screenshots)) {
            screenshots.forEach((screenshot) => {
                form.append('SCREENSHOT', screenshot.content, screenshot.title);
            });
        }
        return apiClient.request<DebugResource.Upload>({
            method: 'POST',
            url: `debug/next/file_upload/${args.debugReportId}/`,
            params: {
                status: DebugStatusEnum.PARTIAL.name,
            },
            data: form,
            timeout: 120000,
        });
    });
    const initiateLegacyDebugReportQuery = useQuery({
        queryKey: ['initiate-legacy-debug-report'],
        queryFn: async () => {
            // window.getErrorReportInfo will only exist in electron
            const errorReportMeta = await electronBridge?.getErrorReportMeta?.();
            const metaBlobObj = {
                isElectron,
                ...(errorReportMeta ?? {}),
                ...(getMetricTags() ?? {}),
            };
            return apiClient
                .request<DebugResource.InitiateDebugSnapShot>({
                    method: 'POST',
                    url: 'debug/initiate/',
                    // @ts-expect-error types here are bad but this works
                    data: {
                        apiVersion: errorReportMeta?.apiVersion ?? 'stub-cad-next-api-version',
                        metaBlob: JSON.stringify(metaBlobObj),
                    },
                })
                .then((response) => {
                    return response.data.data;
                });
        },
        onSuccess(debugReportId) {
            if (debugReportId && isElectron) {
                uploadSystemInformationMutation.mutate({ debugReportId });
                uploadScreenshotsMutation.mutate({ debugReportId });
            }
        },
    });
    const submitLegacyDebugReport = React.useCallback(
        async (id: number, text: string) => {
            const form = new FormData();
            form.append('COMMENTS', text);
            return apiClient.request<DebugResource.Upload>({
                method: 'POST',
                url: `debug/next/file_upload/${id}/`,
                data: form,
            });
        },
        [apiClient]
    );
    const saveCommentsMutation = useMutation(
        async (props: { form: SubmitErrorReportForm; legacyDebugReportId: number }) => {
            const legacyDebugRequest = await submitLegacyDebugReport(
                props.legacyDebugReportId,
                props.form.comments
            );
            return legacyDebugRequest;
        },
        {
            onSuccess() {
                toast({
                    status: 'default',
                    description: 'Error Report Submitted',
                });
                modalControls.close();
            },
        }
    );
    const onSubmit = React.useCallback(
        async (form: SubmitErrorReportForm) => {
            saveCommentsMutation.mutate({
                form,
                legacyDebugReportId: initiateLegacyDebugReportQuery.data!,
            });
        },
        [saveCommentsMutation, initiateLegacyDebugReportQuery.data]
    );
    const modalControls = useFullscreenModalControls();
    return (
        <IntegratedFormProvider
            query={initiateLegacyDebugReportQuery}
            mutation={saveCommentsMutation}
            onSubmit={onSubmit}
            form={form}
        >
            <BlankModal isOpen={true} onClose={() => modalControls.close()}>
                <ModalHeader>Submit an Error</ModalHeader>
                <ModalBody>
                    <FormBody saveErrorMessage={errorOccurredWhile('submitting error report')}>
                        <VStack>
                            {isElectron && screenshots === 'ERROR_GETTING_SCREENSHOTS' ? (
                                <InlineBanner
                                    status="error"
                                    title={
                                        <Text variant="bodyMd">
                                            Failed to get screenshots. You can still submit an error
                                            report but screenshots will not be included.
                                        </Text>
                                    }
                                    style={{
                                        marginBottom: cssVar('arc.space.2'),
                                    }}
                                />
                            ) : undefined}
                            <InlineBanner
                                status="info"
                                title={
                                    <>
                                        <Text variant="bodyMd">
                                            Submitting this error report saves logs for future
                                            troubleshooting and notifies your agency admin/contact
                                            of the submitted error.
                                        </Text>
                                        <Text variant="bodyMd">
                                            To work with the Mark43 Support team on the reported
                                            error, please forward the Error Report email to
                                            support@mark43.com.
                                        </Text>
                                    </>
                                }
                                style={{
                                    marginBottom: cssVar('arc.space.2'),
                                }}
                            />
                            {isElectron ? (
                                <>
                                    <MutationTile
                                        mutation={uploadScreenshotsMutation}
                                        loadingText="Uploading Screenshots"
                                        successText="Screenshots Uploaded"
                                        failureText="Error Uploading Screenshots"
                                    />
                                    <MutationTile
                                        mutation={uploadSystemInformationMutation}
                                        loadingText="Uploading System Information"
                                        successText="System Information Uploaded"
                                        failureText="Error Uploading System Information"
                                    />
                                </>
                            ) : undefined}
                            <FormTextarea
                                textareaProps={{
                                    style: {
                                        height: '120px',
                                    },
                                }}
                                control={form.control}
                                name="comments"
                                label="Comments"
                                rules={{
                                    required: 'Required',
                                }}
                                size="md"
                                placeholder="Please provide a few details about the issue you're having"
                            />
                        </VStack>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack>
                        <FormCancelButton
                            onClick={() => {
                                modalControls.close();
                            }}
                        />
                        <FormSaveButton>Submit</FormSaveButton>
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
}
